import { FC, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { PDFViewer, Font } from '@react-pdf/renderer'

import { setLocalStorageItem, useRouteState } from '@shared/utils'
import { PDFPreviewRouteState } from './state'
import { useLayoutContext } from '@sr/routes/common/layout-provider'
import {
  Button,
  BUTTON_VARIANTS,
  NotificationsOptions,
} from '@shared/ui'
import { ErrorList } from './components'

import { SRErrorModal, SRModal } from '@sr/ui'
import { ROUTE_PATH_CAMPAIGN } from 'apps/social-referent/src/routes/enums'

import { CheckmarkFilled } from '@carbon/icons-react'

import { Generator } from '@qidadev/axis-pdf-creator'

import AeonikLight from "../../../../../../../assets/pdf-report/Aeonik-Light.ttf"
import AeonikLightItalic from "../../../../../../../assets/pdf-report/Aeonik-LightItalic.ttf"
import AeonikRegular from "../../../../../../../assets/pdf-report/Aeonik-Regular.ttf"
import AeonikRegularItalic from "../../../../../../../assets/pdf-report/Aeonik-RegularItalic.ttf"
import AeonikMedium from "../../../../../../../assets/pdf-report/Aeonik-Medium.ttf"
import AeonikMediumItalic from "../../../../../../../assets/pdf-report/Aeonik-MediumItalic.ttf"
import AeonikBold from "../../../../../../../assets/pdf-report/Aeonik-Bold.ttf"
import AeonikBoldItalic from "../../../../../../../assets/pdf-report/Aeonik-BoldItalic.ttf"
import AeonikBlack from "../../../../../../../assets/pdf-report/Aeonik-Black.ttf"
import AeonikBlackItalic from "../../../../../../../assets/pdf-report/Aeonik-BlackItalic.ttf"

import './styles.scss'

type TestParams = {
  page: number
  id: string
}

type Props = {
  name: string
  onSelectTest: (testParams: TestParams) => void
  onPreviewError: () => void
  testOrder: string
  isCompleting: boolean
  statusPostal: 'off' | 'on' | 'disabled'
  sendByPostal: () => void
  dataPostal?: string
  statusEmail: boolean | undefined
  sendByEmail: () => void
  dataEmail?: string
  reportType: number
  generateInform: boolean
  test: any
}

export const registerFonts = () => {
  Font.register({
    family: 'Aeonik',
    fonts: [
      {
        src: AeonikLight,
        fontWeight: 300,
        fontStyle: 'normal',
      },
      {
        src: AeonikLightItalic,
        fontWeight: 300,
        fontStyle: 'italic',
      },
      {
        src: AeonikRegular,
        fontWeight: 400,
        fontStyle: 'normal',
      },
      {
        src: AeonikRegularItalic,
        fontWeight: 400,
        fontStyle: 'italic',
      },
      {
        src: AeonikMedium,
        fontWeight: 500,
        fontStyle: 'normal',
      },
      {
        src: AeonikMediumItalic,
        fontWeight: 500,
        fontStyle: 'italic',
      },
      {
        src: AeonikBold,
        fontWeight: 700,
        fontStyle: 'normal',
      },
      {
        src: AeonikBoldItalic,
        fontWeight: 700,
        fontStyle: 'italic',
      },
      {
        src: AeonikBlack,
        fontWeight: 900,
        fontStyle: 'normal',
      },
      {
        src: AeonikBlackItalic,
        fontWeight: 900,
        fontStyle: 'italic',
      },
    ],
  })

  const hyphenationCallback = (word: string): string[] => {
    return [word]
  }

  Font.registerHyphenationCallback(hyphenationCallback)
}

registerFonts()

export const PDFPreview: FC<Props> = observer(
  ({
    onSelectTest,
    onPreviewError,
    isCompleting,
    statusPostal,
    sendByPostal,
    dataPostal,
    statusEmail,
    sendByEmail,
    dataEmail,
    generateInform,
    reportType,
    test,
  }) => {
    const { testId } = useParams<{ testId: string }>()
    const { t } = useTranslation()

    const [hasSendedPostal, setHasSendedPostal] = useState<boolean>(false)
    const [sentPostalDate, setSentPostalDate] = useState<string | undefined>()
    const [modifiedState, setModifiedState] = useState<'off' | 'on' | 'disabled'>(statusPostal)

    const [emailState, setEmailState] = useState<boolean | undefined>(statusEmail)
    const [sentEmailDate, setSentEmailDate] = useState<string | undefined>()

    const [hasSendedEmail, setHasSendedEmail] = useState<boolean>(false)

    const { setIsLoading } = useLayoutContext()
    const {
      hasError,
      isLoading,
      testList,
      analysisStatus,
      pdfDataParsed,
    } = useRouteState(
      PDFPreviewRouteState,
      { testId },
    )
    const pdfDataMemo = useMemo(() => pdfDataParsed, [pdfDataParsed])

    const handleToggleChange = (state: 'off' | 'on' | 'disabled' | boolean) => {
      setHasSendedPostal(state === 'on')
    }

    useEffect(() => {
      if(statusPostal !== 'on') {
        if(analysisStatus === 3) {
          setModifiedState('off')
        } else {
          setModifiedState('disabled')
        }
      }
      if(generateInform) {
        setModifiedState('off')
      }
      setEmailState(statusEmail)
    }, [statusPostal, statusEmail, analysisStatus, generateInform])

    const closeModalPostal = () => {
      setHasSendedPostal(false)
      setModifiedState('off')
    }

    const handleSendedPostal = () => {
      sendByPostal()
      const currentDate = new Date().toISOString().split('T')[0]
      setLocalStorageItem('date_Sent_Postal', currentDate)
      setSentPostalDate(currentDate)
      setHasSendedPostal(false)
      setModifiedState('on')
      setIsLoading(true)
    }

    const handleEmailChange = () => {
      setHasSendedEmail(true)
    }

    const handleSendedEmail = async () => {
      sendByEmail()
      const currentDate = new Date().toISOString().split('T')[0]
      setLocalStorageItem('date_Sent_Email', currentDate)
      setSentEmailDate(currentDate)
      setHasSendedEmail(false)
    }

    const closeModalEmail = () => {
      setHasSendedEmail(false)
    }

    const isGeneratedDateAfterPatch = test?.genericAnalysisDate > new Date("2025-05-07T00:00:00.000Z")
    const generatedDate = test?.genericAnalysisGenerationDate || (isGeneratedDateAfterPatch ? null : test?.genericAnalysisResultsDate)

    return (
      <>
        <div className="preview">
          <div className="py-0 pl-6 pr-0 xl:pr-6 w-3/6 lg:max-w-3/6 flex flex-col h-full">
            <ErrorList list={testList} onSelectTest={onSelectTest} />

            <div className="w-full h-full flex flex-col items-end justify-end">
              <NotificationsOptions
                title={t('userArea.notifications.emailTitle')}
                subTitle={t('userArea.notifications.emailSubTitle')}
                notificationId="email"
                handleEmailModal={handleEmailChange}
                icon="attach"
                cta="cta_button"
                status={emailState || (analysisStatus === 3)}
                dataSentNotification={dataEmail || sentEmailDate}
              />
              {statusPostal && (
                <NotificationsOptions
                  title={t('userArea.notifications.postalEmailTitle')}
                  subTitle={t('userArea.notifications.postalEmailSubTitle')}
                  notificationId="postal-mail"
                  handleToggleChange={handleToggleChange}
                  icon="print"
                  cta="cta_toggle"
                  status={modifiedState}
                  dataSentNotification={dataPostal || sentPostalDate}
                />
              )}
              <NotificationsOptions
                title={t('userArea.report.generatedDateTitle')}
                subTitle={t('userArea.report.generatedDateSubTitle')}
                notificationId="generated-report"
                icon="catalog"
                cta="no_cta"
                generatedDate={generatedDate}
              />
            </div>
 
            {hasSendedPostal && (
              <SRModal onClose={closeModalPostal}>
                <div
                  className="flex flex-col justify-center items-center gap-4 mb-4 text-primary"
                  data-testid="agenda-schedule-modal"
                >
                  <CheckmarkFilled size={50} />
                  <div className="font-normal text-xl text-black">
                    <p>{t('userArea.notifications.modal.postalEmailDescription')}
                    <strong className='ml-2'>{t('userArea.monitoringArea.services.confirmModal.action')}</strong></p>
                  </div>
                  <div className="flex flex-row align-end gap-2 justify-end w-full">
                    <Button
                      variant={BUTTON_VARIANTS.outlined}
                      onClick={closeModalPostal}
                    >
                      {t('common:cancel')}
                    </Button>
                    <Button onClick={handleSendedPostal}>
                      {t('common:continue')}
                    </Button>
                  </div>
                </div>
              </SRModal>
            )}

            {hasSendedEmail && (
              <SRModal onClose={closeModalEmail}>
                <div
                  className="flex flex-col justify-center items-center gap-4 mb-4 text-primary"
                  data-testid="agenda-schedule-modal"
                >
                  <CheckmarkFilled size={50} />
                  <div className="font-normal text-xl text-center text-black">
                    <p>{t('userArea.notifications.modal.emailDescription')}
                    <strong className='ml-2'>{t('userArea.monitoringArea.services.confirmModal.action')}</strong></p>
                  </div>
                  <div className="flex flex-row align-end gap-2 justify-end w-full">
                    <Button
                      variant={BUTTON_VARIANTS.outlined}
                      onClick={closeModalEmail}
                    >
                      {t('common:cancel')}
                    </Button>
                    <Button onClick={handleSendedEmail}>
                      {t('common:continue')}
                    </Button>
                  </div>
                </div>
              </SRModal>
            )}
          </div>

          {test.isFilled && (
            <div className="bg-primary p-6 max-w-[70%] lg:w-[52%]">
              {isLoading || isCompleting ? (
                <div>
                  <span className="p-3 text-white">
                    {t('common:loadingDownload')}
                  </span>
                </div>
              ) : (
                <PDFViewer className="preview__pdf-container" showToolbar={false}>
                  <Generator data={pdfDataMemo} />
                </PDFViewer>
              )}
            </div>
          )}
        </div>

        {hasError && (
          <SRErrorModal
            onRetry={onPreviewError}
            backUrl={`../${ROUTE_PATH_CAMPAIGN.userNewTest}`}
            mainMessage="analysis:errorModal.previewError"
          />
        )}
      </>
    )
  }
)
